import { useMediaQuery } from '@material-ui/core'
import Add from '@material-ui/icons/Add'
import ArrowBackIosOutlined from '@material-ui/icons/ArrowBackIosOutlined'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import useZone from 'hooks/useZone'
import { TEST_ID_CASHFLOW_NAV_LINK } from 'e2e/playwrightTestIds'
import StyledNavLink from 'layout/headers/elements/StyledNavLink'
import useNavigationLinkClick from 'layout/hooks/useNavigationLinkClick'
import useNavigationLinks, { CONTROL_NAVIGATION_LINK, HOME_NAVIGATION_LINK } from 'layout/hooks/useNavigationLinks'
import ChatDrawerButton from 'layout/widgets/adaChat/ChatDrawerButton'
import { useAdaActive } from 'layout/widgets/adaChat/useAdaActive'
import {
  BaseThemeType,
  Box,
  Button,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  MenuItem,
  ResponsiveOverflowSwitcher,
  ResponsiveOverflowSwitcherContainer,
  Select,
  styled,
} from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ActionType, EventType } from 'types/tracking'
import { DialogHelper } from 'util/misc'
import NavigationIconButtons, { NavigationIconButton } from './NavigationIconButtons'
import NavigationSettingsMenu from './NavigationSettingsMenu'

const NavContainer = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    background: 'rgb(236, 236, 237)',
    bottom: '0px',
    content: "''",
    height: '1px',
    left: '0',
    position: 'absolute',
    width: '100%',
  },
}))

const LinksContainer = styled(ResponsiveOverflowSwitcherContainer)({
  alignItems: 'center',
  display: 'flex',
  columnGap: 12,
  flex: 1,
})

const OpensolarLogoImage = styled('img')({
  height: '30px',
})

const NearmapLogoImage = styled('img')({
  borderRight: '1px solid #E6E6E6',
  height: '20px',
  marginRight: 8,
  paddingRight: 8,
})

const getLogos = (isIcons) =>
  isIcons
    ? {
        nearmapLogo: window.PUBLIC_URL + '/images/nearmap-3-icon.png',
        opensolarLogo: `${window.PUBLIC_URL}/images/logo-icon.svg`,
      }
    : {
        nearmapLogo: window.PUBLIC_URL + '/images/nearmap-3.png',
        opensolarLogo: `${window.PUBLIC_URL}/images/logo.svg`,
      }

interface TopNavigationHeader3Props {
  navigationBackUrl: string
  setAvailableOrgs: (v) => void
  setSelectOrgVisible: (v) => void
}

const TopNavigationHeader3: React.FC<TopNavigationHeader3Props> = ({
  navigationBackUrl,
  setAvailableOrgs,
  setSelectOrgVisible,
}) => {
  const isLogoIcon = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('md'))
  const isNearmapSkin = useSelector((state: any) => state.theme === 'nearmap')
  const briefOrgs = useSelector(authSelectors.getBriefOrgs)
  const proFeaturesEnabled = useSelector(orgSelectors.getProFeaturesEnabled)
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.up('sm'))
  const { active: showChatDrawerButton } = useAdaActive()
  const onNavigationClick = useNavigationLinkClick()

  const { nearmapLogo, opensolarLogo } = getLogos(isLogoIcon)

  const navigationLinks = useNavigationLinks()
  const history = useHistory()
  const translate = useTranslate()
  const zone = useZone()

  const { trackEvent } = useTrackComponent({
    componentKey: 'top_navigation',
    eventName: 'User Viewed Top Navigation',
  })

  const NavigationLinks = navigationLinks.map(({ badgeSeverity, label, key, uiKey, value }) => (
    <StyledNavLink
      badgeSeverity={badgeSeverity}
      key={`m-${key}`}
      uiKey={uiKey}
      to={`/${key}`}
      label={label}
      value={value}
      data-testid={key === 'cashflow' ? TEST_ID_CASHFLOW_NAV_LINK : undefined}
    />
  ))
  const homeNavigationLink = proFeaturesEnabled ? HOME_NAVIGATION_LINK : CONTROL_NAVIGATION_LINK
  const selectNavigationLinks = [homeNavigationLink, ...navigationLinks]

  const activeNavLink = selectNavigationLinks.find(
    ({ key, value }) => key === history.location.pathname.split('/')[1] || value === zone
  )

  return (
    <ComponentVersionsInherit versions={{ button: 3, icon_button: 2 }}>
      <NavContainer
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        padding={isMobile ? '0 16px' : '0 24px'}
      >
        <LinksContainer>
          {navigationBackUrl && (
            <NavigationIconButton
              onClick={() => {
                window.location.href = navigationBackUrl
              }}
              size="small"
              shape="squared"
            >
              <ArrowBackIosOutlined />
            </NavigationIconButton>
          )}
          <StyledNavLink to={`/${homeNavigationLink.key}`} label={homeNavigationLink.label}>
            {isNearmapSkin && <NearmapLogoImage alt="co-branding" src={nearmapLogo} />}
            <OpensolarLogoImage src={opensolarLogo} />
          </StyledNavLink>
          {!isMobile && (
            <ResponsiveOverflowSwitcher
              extraCondition={isMobile}
              renderOverflowing={() => (
                <Box alignItems="center" display="flex">
                  <ComponentVersionsInherit versions={ComponentVersions_3_0}>
                    <Select
                      onChange={(e) => {
                        const navLinkValue = e.target.value
                        const navLink = selectNavigationLinks.find(({ key }) => key === navLinkValue)
                        if (navLink) {
                          onNavigationClick({ label: navLink.label, to: `/${navLink.key}`, value: navLink.value })
                        }
                      }}
                      value={activeNavLink?.key}
                    >
                      {selectNavigationLinks.map(({ key, label }) => (
                        <MenuItem key={key} value={key}>
                          {translate(label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </ComponentVersionsInherit>
                </Box>
              )}
            >
              <Box alignItems="center" display="flex" gridColumnGap={12}>
                {NavigationLinks}
              </Box>
            </ResponsiveOverflowSwitcher>
          )}
        </LinksContainer>
        <Box id="header-profile-area" display="flex" gridGap={12} padding="0px 4px">
          {showChatDrawerButton && <ChatDrawerButton iconColor="primary" />}
          <NavigationIconButtons />
          {isTablet && (
            <Button
              color="default"
              onClick={() => {
                trackEvent(
                  EventType.USER_INTERACTION,
                  { type: ActionType.CLICK },
                  {
                    eventName: 'User Clicked On New Project Button',
                  }
                )
                history.push('/home')
              }}
              startIcon={<Add />}
              variant="contained"
              style={{ lineHeight: 1 }}
            >
              {translate('Project')}
            </Button>
          )}

          <NavigationSettingsMenu
            showOrgSelector={() => {
              DialogHelper.afterOpen()
              setSelectOrgVisible(true)
              setAvailableOrgs(briefOrgs)
            }}
          />
        </Box>
      </NavContainer>
    </ComponentVersionsInherit>
  )
}

// @ts-ignore
export default TopNavigationHeader3
